import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Eye, Pencil, Plus, TicketPercent, Trash2 } from "lucide-react";

const emptyCoupon = {
  code: "",
  description: "",
  discount_type: "percent",
  discount_value: "",
  max_uses: "",
  valid_from: "",
  valid_until: "",
  active: true,
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatDateTime = (value) => (value ? new Date(value).toLocaleString("pt-BR") : "-");

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [newCoupon, setNewCoupon] = useState(emptyCoupon);
  const [editCoupon, setEditCoupon] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [usageError, setUsageError] = useState("");

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/coupons`, { withCredentials: true });
      setCoupons(response.data?.coupons || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar cupons");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const buildPayload = (coupon) => ({
    code: coupon.code,
    description: coupon.description || null,
    discount_type: coupon.discount_type,
    discount_value: Number(coupon.discount_value || 0),
    max_uses: coupon.max_uses ? Number(coupon.max_uses) : null,
    valid_from: coupon.valid_from || null,
    valid_until: coupon.valid_until || null,
    active: coupon.active,
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/coupons`, buildPayload(newCoupon), { withCredentials: true });
      toast.success("Cupom criado com sucesso");
      setShowCreate(false);
      setNewCoupon(emptyCoupon);
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar cupom");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (coupon) => {
    setEditCoupon({
      ...coupon,
      discount_value: coupon.discount_value ?? "",
      max_uses: coupon.max_uses ?? "",
      valid_from: coupon.valid_from ? coupon.valid_from.slice(0, 16) : "",
      valid_until: coupon.valid_until ? coupon.valid_until.slice(0, 16) : "",
    });
    setShowEdit(true);
  };

  const openUsage = async (coupon) => {
    setShowUsage(true);
    setUsageLoading(true);
    setUsageData(null);
    setUsageError("");
    try {
      const response = await axios.get(`${API}/admin/coupons/${coupon.coupon_id}/usages`, { withCredentials: true });
      setUsageData(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao carregar uso do cupom";
      setUsageError(message);
      toast.error(message);
    } finally {
      setUsageLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editCoupon) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/coupons/${editCoupon.coupon_id}`,
        {
          description: editCoupon.description || null,
          discount_type: editCoupon.discount_type,
          discount_value: Number(editCoupon.discount_value || 0),
          max_uses: editCoupon.max_uses ? Number(editCoupon.max_uses) : null,
          valid_from: editCoupon.valid_from || null,
          valid_until: editCoupon.valid_until || null,
          active: editCoupon.active,
        },
        { withCredentials: true }
      );
      toast.success("Cupom atualizado");
      setShowEdit(false);
      setEditCoupon(null);
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar cupom");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Deseja excluir o cupom ${coupon.code}?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${coupon.coupon_id}`, { withCredentials: true });
      toast.success("Cupom excluído");
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir cupom");
    }
  };

  const cards = useMemo(() => {
    const active = coupons.filter((coupon) => coupon.active).length;
    const used = coupons.filter((coupon) => Number(coupon.used_count || 0) > 0).length;
    return { active, used, total: coupons.length };
  }, [coupons]);

  return (
    <AdminLayout>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-serif text-3xl text-navy">Cupons de Desconto</h1>
            <p className="text-slate-600">Crie campanhas promocionais para a compra dos diagnósticos.</p>
          </div>
          <Button className="bg-navy hover:bg-navy/90" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo cupom
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-sm text-slate-500">Cupons cadastrados</p><p className="mt-3 text-3xl font-bold text-navy">{cards.total}</p></CardContent></Card>
          <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-sm text-slate-500">Ativos</p><p className="mt-3 text-3xl font-bold text-emerald-600">{cards.active}</p></CardContent></Card>
          <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-sm text-slate-500">Já utilizados</p><p className="mt-3 text-3xl font-bold text-amber-600">{cards.used}</p></CardContent></Card>
        </div>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Lista de cupons</CardTitle>
            <CardDescription>O cliente poderá informar o código antes de abrir o checkout do diagnóstico.</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy" />
              </div>
            ) : (
              <table className="w-full text-sm min-w-[980px]">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="py-3 pr-3">Código</th>
                    <th className="py-3 pr-3">Descrição</th>
                    <th className="py-3 pr-3">Desconto</th>
                    <th className="py-3 pr-3">Validade</th>
                    <th className="py-3 pr-3">Quantidade</th>
                    <th className="py-3 pr-3">Status</th>
                    <th className="py-3 pr-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon.coupon_id} className="border-b align-top">
                      <td className="py-3 pr-3 font-semibold text-navy uppercase">{coupon.code}</td>
                      <td className="py-3 pr-3 text-slate-600">{coupon.description || "-"}</td>
                      <td className="py-3 pr-3">
                        {coupon.discount_type === "percent"
                          ? `${Number(coupon.discount_value || 0).toFixed(2)}%`
                          : formatCurrency(coupon.discount_value)}
                      </td>
                      <td className="py-3 pr-3 text-slate-600">
                        <div>{coupon.valid_from ? new Date(coupon.valid_from).toLocaleString("pt-BR") : "Sem início"}</div>
                        <div>{coupon.valid_until ? new Date(coupon.valid_until).toLocaleString("pt-BR") : "Sem fim"}</div>
                      </td>
                      <td className="py-3 pr-3 text-slate-600">
                        <div>Liberados: {coupon.max_uses ?? "Ilimitado"}</div>
                        <div>Usados: {coupon.used_count || 0}</div>
                        <div>Restantes: {coupon.max_uses ? Math.max(0, Number(coupon.max_uses) - Number(coupon.used_count || 0)) : "Ilimitado"}</div>
                      </td>
                      <td className="py-3 pr-3">
                        <Badge className={coupon.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>
                          {coupon.active ? "Ativo" : "Inativo"}
                        </Badge>
                      </td>
                      <td className="py-3 pr-3">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openUsage(coupon)}>
                            <Eye className="h-4 w-4 mr-1" />
                            Acompanhar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(coupon)}>
                            <Pencil className="h-4 w-4 mr-1" />
                            Editar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(coupon)}>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Excluir
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Novo cupom</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Código</Label>
                  <Input value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} placeholder="EX: LANCAMENTO10" required />
                </div>
                <div>
                  <Label>Tipo de desconto</Label>
                  <Select value={newCoupon.discount_type} onValueChange={(value) => setNewCoupon({ ...newCoupon, discount_type: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percentual (%)</SelectItem>
                      <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Valor do desconto</Label>
                  <Input type="number" min="0" step="0.01" value={newCoupon.discount_value} onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: e.target.value })} required />
                </div>
                <div>
                  <Label>Quantidade de cupons liberados</Label>
                  <Input type="number" min="1" value={newCoupon.max_uses} onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value })} placeholder="Opcional - deixe em branco para ilimitado" />
                </div>
              </div>
              <div>
                <Label>Descrição</Label>
                <Input value={newCoupon.description} onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })} placeholder="Campanha de lançamento" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Válido a partir de</Label>
                  <Input type="datetime-local" value={newCoupon.valid_from} onChange={(e) => setNewCoupon({ ...newCoupon, valid_from: e.target.value })} />
                </div>
                <div>
                  <Label>Válido até</Label>
                  <Input type="datetime-local" value={newCoupon.valid_until} onChange={(e) => setNewCoupon({ ...newCoupon, valid_until: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={newCoupon.active} onCheckedChange={(checked) => setNewCoupon({ ...newCoupon, active: checked })} />
                <span className="text-sm text-slate-600">Cupom ativo</span>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                <Button type="submit" className="bg-navy hover:bg-navy/90" disabled={creating}>
                  <TicketPercent className="h-4 w-4 mr-2" />
                  {creating ? "Salvando..." : "Criar cupom"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Editar cupom</DialogTitle></DialogHeader>
            {editCoupon && (
              <form onSubmit={handleEdit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Código</Label>
                    <Input value={editCoupon.code} disabled />
                  </div>
                  <div>
                    <Label>Tipo de desconto</Label>
                    <Select value={editCoupon.discount_type} onValueChange={(value) => setEditCoupon({ ...editCoupon, discount_type: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">Percentual (%)</SelectItem>
                        <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Valor do desconto</Label>
                    <Input type="number" min="0" step="0.01" value={editCoupon.discount_value} onChange={(e) => setEditCoupon({ ...editCoupon, discount_value: e.target.value })} required />
                  </div>
                  <div>
                    <Label>Quantidade de cupons liberados</Label>
                    <Input type="number" min="1" value={editCoupon.max_uses} onChange={(e) => setEditCoupon({ ...editCoupon, max_uses: e.target.value })} placeholder="Opcional - deixe em branco para ilimitado" />
                  </div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
                  <div>Liberados: {editCoupon.max_uses || "Ilimitado"}</div>
                  <div>Usados: {editCoupon.used_count || 0}</div>
                  <div>Restantes: {editCoupon.max_uses ? Math.max(0, Number(editCoupon.max_uses) - Number(editCoupon.used_count || 0)) : "Ilimitado"}</div>
                </div>
                <div>
                  <Label>Descrição</Label>
                  <Input value={editCoupon.description || ""} onChange={(e) => setEditCoupon({ ...editCoupon, description: e.target.value })} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Válido a partir de</Label>
                    <Input type="datetime-local" value={editCoupon.valid_from || ""} onChange={(e) => setEditCoupon({ ...editCoupon, valid_from: e.target.value })} />
                  </div>
                  <div>
                    <Label>Válido até</Label>
                    <Input type="datetime-local" value={editCoupon.valid_until || ""} onChange={(e) => setEditCoupon({ ...editCoupon, valid_until: e.target.value })} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={!!editCoupon.active} onCheckedChange={(checked) => setEditCoupon({ ...editCoupon, active: checked })} />
                  <span className="text-sm text-slate-600">Cupom ativo</span>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowEdit(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-navy hover:bg-navy/90" disabled={saving}>
                    {saving ? "Salvando..." : "Salvar alterações"}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showUsage} onOpenChange={setShowUsage}>
          <DialogContent className="max-w-5xl">
            <DialogHeader><DialogTitle>Acompanhamento de cupom</DialogTitle></DialogHeader>
            {usageLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy" />
              </div>
            ) : usageError ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                {usageError}
              </div>
            ) : usageData ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <Card className="border-0 bg-slate-50 shadow-none">
                    <CardContent className="p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Cupom</p>
                      <p className="mt-2 text-lg font-semibold text-navy">{usageData.coupon?.code}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 bg-slate-50 shadow-none">
                    <CardContent className="p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Liberados</p>
                      <p className="mt-2 text-lg font-semibold text-navy">{usageData.usage_summary?.released_count ?? "Ilimitado"}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 bg-slate-50 shadow-none">
                    <CardContent className="p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Usados</p>
                      <p className="mt-2 text-lg font-semibold text-amber-600">{usageData.usage_summary?.used_count || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 bg-slate-50 shadow-none">
                    <CardContent className="p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Restantes</p>
                      <p className="mt-2 text-lg font-semibold text-emerald-600">{usageData.usage_summary?.remaining_count ?? "Ilimitado"}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full min-w-[900px] text-sm">
                    <thead className="bg-slate-50 text-left text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Empresa</th>
                        <th className="px-4 py-3">Cliente</th>
                        <th className="px-4 py-3">E-mail</th>
                        <th className="px-4 py-3">Pagamento</th>
                        <th className="px-4 py-3">Desconto</th>
                        <th className="px-4 py-3">Forma</th>
                        <th className="px-4 py-3">Data de uso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(usageData.usages || []).length > 0 ? (
                        usageData.usages.map((usage) => (
                          <tr key={usage.payment_id} className="border-t">
                            <td className="px-4 py-3 text-slate-700">{usage.company_name}</td>
                            <td className="px-4 py-3 text-slate-700">{usage.user_name}</td>
                            <td className="px-4 py-3 text-slate-600">{usage.user_email}</td>
                            <td className="px-4 py-3 text-slate-700">{formatCurrency(usage.amount)}</td>
                            <td className="px-4 py-3 text-slate-700">{formatCurrency(usage.discount_amount)}</td>
                            <td className="px-4 py-3 text-slate-600">{usage.payment_method_label}</td>
                            <td className="px-4 py-3 text-slate-600">{formatDateTime(usage.paid_at || usage.created_at)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="px-4 py-6 text-center text-slate-500" colSpan={7}>
                            Este cupom ainda não foi utilizado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCoupons;
